import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { beSalonStripeAccountWrite } from 'actions/salon/bePutSalonStripe.action';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import StripeAccountVerifierModal from 'components/modal/StripeAccountVerifierModal.component';
import StripeStatusBadges from 'components/settings/StripeStatusBadges.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import IN from 'constants/in';
import S from 'constants/string.const';
import SALON_STRIPE_STATUS from 'constants/stripeStatus.const';
import useCallbackUnignored from 'hooks/useCallbackUnignored.hook';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useCurrentSalonId, withSalonRestriction } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonRestrictedToCriticalServices } from 'selectors/currentSalon.selectors';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { locationAssign, locationReload } from 'utils/dom/location.util';
import logger from 'utils/logger.util';
import { isValidUrl } from 'utils/predicates/isValid.util';

import BeforeSetupStripeModal from 'settings/online-booking/modals/BeforeSetupStripeModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './RequestDepositSetup.module.scss';

const CTAG = 'RequestDepositSetup';
const L = logger('RequestDepositSetup');

const ROADMAP_LINK = 'https://help.getslick.com/en/articles/5685099-options-for-stripe-setup-slick-pay';
const USE_STRIPE_BADGES = false;

const RequestDepositSetup = (props) => {
  const {
    random_key: randomKey,
    isFetchingRandomKey,
    fetchSalonState,
    gotoSalonStripeDashboard,
    isRestrictedToCriticalServices,
    isRestrictionCheckEnabled,
    enableRestrictionCheck,
    disableRestrictionCheck,
  } = props;
  const { up: getStripeLink } = usePrepareWriteOnly(beSalonStripeAccountWrite);

  const { value: valueSettings, isLoading, error } = useNcobSettings();

  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [stripeModal] = useState(false);
  const [isBeforeSetupModalOpen, setBeforeSetupModal] = useState(false);

  const { hasNcobStripeAccount, salonStripeAccountStatus } = valueSettings || {};

  const isComplete = salonStripeAccountStatus === SALON_STRIPE_STATUS.complete;
  const isRestricted = salonStripeAccountStatus === SALON_STRIPE_STATUS.restricted;
  const isIncomplete = salonStripeAccountStatus === SALON_STRIPE_STATUS.incomplete;

  const showCompleteBanner = isComplete && !isRestrictedToCriticalServices;
  const showUnverifiedBanner = isComplete && isRestrictedToCriticalServices;
  const showRestrictedBanner = isRestricted; // && !isRestrictedToCriticalServices; TBD
  const showIncompleteBanner = isIncomplete;

  const canLoginToStripe = isComplete || isRestricted;

  useEffect(() => {
    if (!randomKey && !isFetchingRandomKey) {
      L.unignore('useEffect()', 'fetchSalonState:', fetchSalonState(fromAnyToString(salonId)));
    }
  }, [fetchSalonState, isFetchingRandomKey, randomKey, salonId]);

  const handleSetupOpen = useCallback(() => setBeforeSetupModal(true), []);
  const handleSetupClose = useCallback(() => setBeforeSetupModal(false), []);

  const handleVerify = useCallback(() => {
    if (isRestrictedToCriticalServices) enableRestrictionCheck();
  }, [enableRestrictionCheck, isRestrictedToCriticalServices]);

  const handleCompleteSetup = useCallbackUnignored(
    async () => {
      const FTAG = 'handleCompleteSetup()';

      const result = await getStripeLink();

      const url =
        result?.json?.stripeLink ||
        result?.originalJson?.results?.stripe_link ||
        result?.originalJson?.stripe_link ||
        null;

      if (url) {
        L.debug(FTAG, 'Opening stripe URL:', url);
        locationAssign({ url });
        return;
      }

      locationReload({ tag: 'RequestDepositSetup' });

      if (!isValidUrl(url)) L.warn(FTAG, 'Invalid stripe url:', url);

      if (result?.ok) {
        L.warn(FTAG, 'Stripe link not received.');
      } else {
        L.warn(FTAG, 'Stripe setup failed.', 'result:', result);
      }
    },
    [getStripeLink],
    CTAG,
    'handleCompleteSetup()',
  );

  L.debug('()', isLoading, error, { valueSettings });

  return (
    <div
      data-bem="RequestDepositSetup"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {hasNcobStripeAccount ? (
        <div className={CN.content}>
          <div className={CN.title}>
            {t('onlineBooking.generalSettings.businessStripeAccount.header1')}
            {USE_STRIPE_BADGES && <StripeStatusBadges tag="RequestDepositSetup" />}
          </div>
          {showCompleteBanner && (
            <div className={CN.alertOuter}>
              {t('onlineBooking.generalSettings.businessStripeAccount.completeBannerAlertPart1')}
              <Space bold>{t('onlineBooking.generalSettings.businessStripeAccount.completeBannerAlertPart2')}</Space>
            </div>
          )}
          {showUnverifiedBanner && (
            <div className={CN.alertOuter}>
              <AlertPanel tag="RequestDepositSetup__alert" variant="warningPanel">
                <div className={CN.alertContent}>
                  <Icon variant="warning" />
                  <div className={CN.alertText}>
                    {t('onlineBooking.generalSettings.businessStripeAccount.unverifiedBannerAlertPart1')}
                    <Space bold>
                      {t('onlineBooking.generalSettings.businessStripeAccount.unverifiedBannerAlertPart2')}{' '}
                    </Space>
                    {t('onlineBooking.generalSettings.businessStripeAccount.unverifiedBannerAlertPart3')}
                  </div>
                </div>
              </AlertPanel>
            </div>
          )}

          {showRestrictedBanner && (
            <div className={CN.alertOuter}>
              <AlertPanel tag="RequestDepositSetup__alert" variant="warningPanel">
                <div className={CN.alertContent}>
                  <Icon variant="warning" />
                  <div className={CN.alertText}>
                    {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart1')}{' '}
                    <Space bold>
                      {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart2')}
                    </Space>{' '}
                    {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart3')}
                    <Space bold>
                      {' '}
                      {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart4')}
                    </Space>
                    <Space bold>
                      {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart5')}{' '}
                    </Space>{' '}
                    {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart6')}
                    <Space bold>
                      {' '}
                      {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart7')}
                    </Space>{' '}
                    {t('onlineBooking.generalSettings.businessStripeAccount.restrictedBannerAlertPart8')}
                  </div>
                </div>
              </AlertPanel>
            </div>
          )}

          {showIncompleteBanner && (
            <div className={CN.alertOuter}>
              <AlertPanel tag="RequestDepositSetup__incomplete-alert" variant="alert">
                <div>
                  {t('onlineBooking.generalSettings.businessStripeAccount.incompleteBannerAlertPart1')} {S.party}{' '}
                  {t('onlineBooking.generalSettings.businessStripeAccount.incompleteBannerAlertPart2')}{' '}
                  <Space bold>
                    {t('onlineBooking.generalSettings.businessStripeAccount.incompleteBannerAlertPart3')}
                  </Space>{' '}
                  {t('onlineBooking.generalSettings.businessStripeAccount.incompleteBannerAlertPart4')}
                </div>
              </AlertPanel>
            </div>
          )}

          <div className={CN.buttonsContainer}>
            <div className={CN.relative}>
              {canLoginToStripe && (
                <Button
                  tag="RequestDepositSetup__view-button"
                  onClick={gotoSalonStripeDashboard}
                  variant="outline"
                  xSpace
                >
                  {t('onlineBooking.generalSettings.businessStripeAccount.loginStripe')}
                </Button>
              )}
              <TooltipForReceptionist where={IN.btnPayment} />
            </div>
            {isIncomplete && (
              <Button
                tag="RequestDepositSetup__complete-setup-button"
                onClick={handleCompleteSetup}
                variant="outline"
                xSpace
              >
                {t('onlineBooking.generalSettings.completeSetup')}
              </Button>
            )}
            {isComplete && isRestrictedToCriticalServices && (
              <div>
                <Button tag="RequestDepositSetup__verify-button" onClick={handleVerify} variant="primary" xSpace>
                  {t('onlineBooking.generalSettings.businessStripeAccount.verifyIdentity')}
                </Button>

                <StripeAccountVerifierModal
                  tag="RequestDepositSetup__verify-modal"
                  enabled={isRestrictionCheckEnabled}
                  onCancel={disableRestrictionCheck}
                  onClose={disableRestrictionCheck}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className={CN.content}>
            <div className={CN.title}>{t('onlineBooking.generalSettings.businessStripeAccount.header1')}</div>
            <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description1')}</div>
            <div className={CN.buttonsContainer}>
              <div className={CN.relative}>
                <Button tag="RequestDepositSetup__setup-button" onClick={handleSetupOpen} variant="primary">
                  {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                </Button>

                <TooltipForReceptionist where={IN.btnSetup} />
              </div>
              <ExternalLink link={ROADMAP_LINK} variant="primary">
                {t('onlineBooking.generalSettings.needHelp')}
              </ExternalLink>
            </div>
          </div>
          <div className={CN.imageContainer} />
        </>
      )}

      {stripeModal && (
        <StripeModal>
          <div className="spinner-text">{t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}</div>
        </StripeModal>
      )}

      <BeforeSetupStripeModal isOpen={isBeforeSetupModalOpen} onClose={handleSetupClose} />
    </div>
  );
};

const RequestDepositSetupHoc = connect((state) => ({
  isRestrictedToCriticalServices: selectCurrentSalonRestrictedToCriticalServices(state),
}))(withSalonRestriction(RequestDepositSetup));

export default RequestDepositSetupHoc;
